<template>
    <div class="template-editor">
        <div class="editor-header">
            <h4>编辑模板字段</h4>
            <span class="tips">提示：从左侧拖拽字段到PDF上，或点击字段后点击PDF定位</span>
        </div>
        
        <div class="editor-body">
            <div class="field-list">
                <h5>可用字段</h5>
                <div class="fields">
                    <div 
                        v-for="field in availableFields" 
                        :key="field.name"
                        class="field-item"
                        :class="{ active: selectedField && selectedField.name === field.name, disabled: isFieldUsed(field.name) }"
                        @click="selectField(field)"
                        draggable="true"
                        @dragstart="handleDragStart(field, $event)"
                    >
                        <span class="field-icon">📋</span>
                        <span>{{ field.label }}</span>
                        <span v-if="isFieldUsed(field.name)" class="used-tag">已添加</span>
                    </div>
                </div>
            </div>
            
            <div class="pdf-editor">
                <div 
                    class="pdf-container" 
                    ref="pdfContainer" 
                    @click="handlePdfClick" 
                    @mousemove="handleMouseMove"
                    @dragover.prevent="handleDragOver"
                    @drop="handleDrop"
                    @mouseup="handleMouseUp"
                >
                    <canvas ref="pdfCanvas"></canvas>
                    
                    <div 
                        v-for="(field, index) in fieldPositions" 
                        :key="index"
                        class="field-marker"
                        :style="getFieldMarkerStyle(field)"
                        @mousedown.stop="startDrag(index, $event)"
                        @click.stop="editField(index)"
                    >
                        <span class="field-label">{{ field.label }}</span>
                        <span class="field-delete" @click.stop="deleteField(index)">×</span>
                    </div>
                    
                    <div v-if="selectedField && !isDragging" class="cursor-indicator" :style="cursorIndicatorStyle">
                        {{ selectedField.label }}
                    </div>
                </div>
                
                <div class="position-info">
                    <div class="page-controls">
                        <button @click="prevPage" :disabled="currentPage <= 1">上一页</button>
                        <span>{{ currentPage }} / {{ totalPages }}</span>
                        <button @click="nextPage" :disabled="currentPage >= totalPages">下一页</button>
                    </div>
                    <span>鼠标位置: ({{ mouseX }}, {{ mouseY }}) | 缩放: {{ Math.round(scale * 100) }}%</span>
                    <div class="zoom-controls">
                        <button @click="zoomOut" :disabled="scale <= 0.3">-</button>
                        <span>{{ Math.round(scale * 100) }}%</span>
                        <button @click="zoomIn" :disabled="scale >= 2">+</button>
                        <button @click="resetZoom">100%</button>
                    </div>
                </div>
            </div>
            
            <div class="field-properties" v-if="editingField !== null">
                <h5>字段属性</h5>
                <div class="properties">
                    <div class="property">
                        <label>字段名称</label>
                        <span>{{ fieldPositions[editingField] && fieldPositions[editingField].label }}</span>
                    </div>
                    <div class="property">
                        <label>X坐标</label>
                        <input type="number" v-model.number="fieldPositions[editingField].x" />
                    </div>
                    <div class="property">
                        <label>Y坐标</label>
                        <input type="number" v-model.number="fieldPositions[editingField].y" />
                    </div>
                    <div class="property">
                        <label>字体大小</label>
                        <input type="number" v-model.number="fieldPositions[editingField].fontSize" />
                    </div>
                    <div class="property">
                        <label>示例值</label>
                        <span>{{ getFieldSample(fieldPositions[editingField] && fieldPositions[editingField].name) }}</span>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="editor-footer">
            <el-button @click="cancel">取消</el-button>
            <el-button type="primary" @click="saveConfig">保存配置</el-button>
        </div>
    </div>
</template>

<script>
    import { TYBM_Env } from '../../env.js';
    import { TYBM_Tools } from '../../tools.js';
    const pdfjsLib = window['pdfjs-dist/build/pdf'];

    export default {
        name: 'NoticeTemplateEditor',
        props: {
            templateId: {
                type: String,
                required: true
            },
            templateName: {
                type: String,
                default: ''
            }
        },
        data() {
                return {
                    availableFields: [],
                    fieldPositions: [],
                    selectedField: null,
                    editingField: null,
                    mouseX: 0,
                    mouseY: 0,
                    mouseScreenX: 0,
                    mouseScreenY: 0,
                    isDragging: false,
                    dragIndex: -1,
                    dragOffsetX: 0,
                    dragOffsetY: 0,
                    originalWidth: 612,
                    originalHeight: 792,
                    totalPages: 1,
                    currentPage: 1,
                    scale: 1,
                    pdfDocument: null,
                    renderedPage: null,
                    pageInfo: []
                };
            },
        computed: {
            cursorIndicatorStyle() {
                return {
                    left: this.mouseScreenX + 'px',
                    top: this.mouseScreenY + 'px'
                };
            }
        },
        mounted() {
            this.loadFields();
            this.loadConfig();
            document.addEventListener('mousemove', this.handleGlobalMouseMove);
            document.addEventListener('mouseup', this.handleGlobalMouseUp);
            window.addEventListener('resize', this.handleResize);
        },
        beforeDestroy() {
            document.removeEventListener('mousemove', this.handleGlobalMouseMove);
            document.removeEventListener('mouseup', this.handleGlobalMouseUp);
            window.removeEventListener('resize', this.handleResize);
        },
        methods: {
            handleResize() {
                this.adjustScale();
            },
            loadFields() {
                const api_name = '/Notice/GetFields';
                const url = TYBM_Env.GetApiUrl(api_name);
                
                TYBM_Tools.AxiosPostEx(this, api_name, url, {}, { loading: false, show_error: false })
                    .then(response => {
                        if (response.code === 'ok') {
                            this.availableFields = response.data || [];
                        }
                    })
                    .catch(() => {});
            },
            loadConfig() {
                const api_name = '/Notice/GetFieldConfig';
                const url = TYBM_Env.GetApiUrl(api_name) + '?templateId=' + this.templateId;
                
                TYBM_Tools.AxiosPostEx(this, api_name, url, {}, { loading: false, show_error: false })
                    .then(response => {
                        if (response.code === 'ok') {
                            this.fieldPositions = response.data || [];
                        }
                    })
                    .catch(() => {});
            },
            loadPdf() {
                const api_name = '/Notice/PreviewTemplate';
                const url = TYBM_Env.GetApiUrl(api_name) + '?id=' + this.templateId + '&t=' + Date.now();
                
                const xhr = new XMLHttpRequest();
                xhr.open('GET', url, true);
                xhr.responseType = 'blob';
                
                xhr.onload = () => {
                    if (xhr.status === 200) {
                        const contentType = xhr.getResponseHeader('Content-Type');
                        const blob = xhr.response;
                        
                        if (contentType && contentType.indexOf('pdf') === -1 && blob.size < 1000) {
                            const reader = new FileReader();
                            reader.onload = () => {
                                this.$message.error('加载PDF失败，后端返回: ' + reader.result);
                                this.renderPlaceholder();
                            };
                            reader.readAsText(blob);
                            return;
                        }
                        
                        if (blob.size > 0) {
                            const blobUrl = URL.createObjectURL(blob);
                            
                            pdfjsLib.getDocument(blobUrl).promise.then(pdf => {
                                this.pdfDocument = pdf;
                                this.totalPages = pdf.numPages;
                                this.pageInfo = [];
                                for (let i = 1; i <= pdf.numPages; i++) {
                                    pdf.getPage(i).then(page => {
                                        const viewport = page.getViewport({ scale: 1 });
                                        this.$set(this.pageInfo, i, {
                                            width: viewport.width,
                                            height: viewport.height
                                        });
                                    });
                                }
                                this.renderPage(1);
                            }).catch(error => {
                                this.$message.error('PDF解析失败: ' + error.message);
                                this.renderPlaceholder();
                            });
                        } else {
                            this.$message.error('PDF文件为空');
                            this.renderPlaceholder();
                        }
                    } else {
                        this.$message.error('加载PDF失败，状态码: ' + xhr.status);
                        this.renderPlaceholder();
                    }
                };
                
                xhr.onerror = () => {
                    this.$message.error('网络请求失败，请检查后端服务是否运行');
                    this.renderPlaceholder();
                };
                
                xhr.send();
            },
            renderPage(pageNum) {
                if (!this.pdfDocument) return;
                
                this.currentPage = pageNum;
                this.pdfDocument.getPage(pageNum).then(page => {
                    const viewport = page.getViewport({ scale: 1 });
                    
                    this.originalWidth = viewport.width;
                    this.originalHeight = viewport.height;
                    
                    this.$nextTick(() => {
                        this.adjustScale();
                        this.drawPage(page);
                    });
                });
            },
            drawPage(page) {
                const canvas = this.$refs.pdfCanvas;
                const ctx = canvas.getContext('2d');
                
                const scaledWidth = this.originalWidth * this.scale;
                const scaledHeight = this.originalHeight * this.scale;
                
                canvas.width = scaledWidth;
                canvas.height = scaledHeight;
                
                const viewport = page.getViewport({ scale: this.scale });
                
                const renderContext = {
                    canvasContext: ctx,
                    viewport: viewport
                };
                
                page.render(renderContext).promise.then(() => {
                    this.renderedPage = page;
                });
            },
            refreshCanvas() {
                if (this.renderedPage) {
                    this.drawPage(this.renderedPage);
                }
            },
            renderPlaceholder() {
                const canvas = this.$refs.pdfCanvas;
                const ctx = canvas.getContext('2d');
                
                this.originalWidth = 612;
                this.originalHeight = 792;
                
                this.$nextTick(() => {
                    this.adjustScale();
                    
                    const scaledWidth = this.originalWidth * this.scale;
                    const scaledHeight = this.originalHeight * this.scale;
                    
                    canvas.width = scaledWidth;
                    canvas.height = scaledHeight;
                    
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, scaledWidth, scaledHeight);
                    ctx.strokeStyle = '#cccccc';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(10, 10, scaledWidth - 20, scaledHeight - 20);
                    ctx.fillStyle = '#666666';
                    ctx.font = '20px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText('录取通知书模板预览', scaledWidth / 2, scaledHeight / 2);
                    ctx.font = '14px Arial';
                    ctx.fillText('(PDF加载失败，请检查后端服务)', scaledWidth / 2, scaledHeight / 2 + 24);
                });
            },
            adjustScale() {
                const pdfContainer = this.$refs.pdfContainer;
                if (pdfContainer) {
                    const containerWidth = pdfContainer.clientWidth - 20;
                    const containerHeight = pdfContainer.clientHeight - 20;
                    
                    if (containerWidth <= 0 || containerHeight <= 0) {
                        this.scale = 0.5;
                        return;
                    }
                    
                    const scaleByWidth = containerWidth / this.originalWidth;
                    const scaleByHeight = containerHeight / this.originalHeight;
                    
                    this.scale = Math.min(scaleByWidth, scaleByHeight);
                    
                    if (this.scale <= 0.1) {
                        this.scale = 0.5;
                    }
                    
                    if (this.renderedPage) {
                        setTimeout(() => {
                            this.drawPage(this.renderedPage);
                        }, 50);
                    }
                }
            },
            getFieldMarkerStyle(field) {
                return {
                    left: field.x * this.scale + 'px',
                    top: field.y * this.scale + 'px',
                    fontSize: field.fontSize * this.scale + 'px'
                };
            },
            isFieldUsed(fieldName) {
                return this.fieldPositions.some(f => f.name === fieldName);
            },
            selectField(field) {
                if (this.isFieldUsed(field.name)) {
                    this.$message.warning('该字段已添加，同一个字段只能添加一次');
                    return;
                }
                this.selectedField = field;
                this.editingField = null;
            },
            handleDragStart(field, event) {
                if (this.isFieldUsed(field.name)) {
                    event.preventDefault();
                    this.$message.warning('该字段已添加，同一个字段只能添加一次');
                    return;
                }
                event.dataTransfer.setData('field', JSON.stringify(field));
                event.dataTransfer.effectAllowed = 'copy';
            },
            handleDragOver(event) {
                event.dataTransfer.dropEffect = 'copy';
            },
            handleDrop(event) {
                const fieldData = event.dataTransfer.getData('field');
                if (!fieldData) return;
                
                const field = JSON.parse(fieldData);
                
                if (this.isFieldUsed(field.name)) {
                    this.$message.warning('该字段已添加，同一个字段只能添加一次');
                    return;
                }
                
                const rect = event.currentTarget.getBoundingClientRect();
                const screenX = event.clientX - rect.left;
                const screenY = event.clientY - rect.top;
                
                const x = screenX / this.scale;
                const y = screenY / this.scale;
                
                this.addFieldAtPosition(field, x, y);
                this.$message.success('已添加字段: ' + field.label);
            },
            handlePdfClick(event) {
                if (this.isDragging || this.editingField !== null) return;
                
                const rect = event.currentTarget.getBoundingClientRect();
                const screenX = event.clientX - rect.left;
                const screenY = event.clientY - rect.top;
                
                const x = screenX / this.scale;
                const y = screenY / this.scale;
                
                if (this.selectedField) {
                    if (this.isFieldUsed(this.selectedField.name)) {
                        this.$message.warning('该字段已添加，同一个字段只能添加一次');
                        return;
                    }
                    this.addFieldAtPosition(this.selectedField, x, y);
                    this.$message.success('已添加字段: ' + this.selectedField.label);
                }
            },
            addFieldAtPosition(field, x, y) {
                this.fieldPositions.push({
                    name: field.name,
                    label: field.label,
                    x: x,
                    y: y,
                    fontSize: 16
                });
            },
            handleMouseMove(event) {
                const rect = event.currentTarget.getBoundingClientRect();
                this.mouseScreenX = event.clientX - rect.left;
                this.mouseScreenY = event.clientY - rect.top;
                this.mouseX = this.mouseScreenX / this.scale;
                this.mouseY = this.mouseScreenY / this.scale;
            },
            handleGlobalMouseMove(event) {
                if (this.isDragging && this.dragIndex >= 0) {
                    const pdfContainer = this.$refs.pdfContainer;
                    if (pdfContainer) {
                        const rect = pdfContainer.getBoundingClientRect();
                        const screenX = event.clientX - rect.left;
                        const screenY = event.clientY - rect.top;
                        
                        const x = screenX / this.scale;
                        const y = screenY / this.scale;
                        
                        this.fieldPositions[this.dragIndex].x = Math.max(0, Math.min(this.originalWidth, x - this.dragOffsetX));
                        this.fieldPositions[this.dragIndex].y = Math.max(0, Math.min(this.originalHeight, y - this.dragOffsetY));
                    }
                }
            },
            handleGlobalMouseUp() {
                if (this.isDragging) {
                    this.isDragging = false;
                    this.dragIndex = -1;
                }
            },
            handleMouseUp() {
                if (this.isDragging) {
                    this.isDragging = false;
                    this.dragIndex = -1;
                }
            },
            startDrag(index, event) {
                this.isDragging = true;
                this.dragIndex = index;
                this.dragOffsetX = event.offsetX / this.scale || 0;
                this.dragOffsetY = event.offsetY / this.scale || 0;
                this.selectedField = null;
                this.editingField = null;
                
                event.preventDefault();
            },
            editField(index) {
                this.editingField = index;
                this.selectedField = null;
            },
            deleteField(index) {
                this.$confirm('确定要删除这个字段吗?', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                }).then(() => {
                    this.fieldPositions.splice(index, 1);
                    this.editingField = null;
                    this.$message.success('删除成功');
                }).catch(() => {});
            },
            zoomIn() {
                this.scale = Math.min(2, this.scale + 0.1);
                this.refreshCanvas();
            },
            zoomOut() {
                this.scale = Math.max(0.3, this.scale - 0.1);
                this.refreshCanvas();
            },
            resetZoom() {
                this.scale = 1;
                if (this.$refs.pdfContainer) {
                    this.$refs.pdfContainer.scrollLeft = 0;
                    this.$refs.pdfContainer.scrollTop = 0;
                }
                this.refreshCanvas();
            },
            prevPage() {
                if (this.currentPage > 1) {
                    this.renderPage(this.currentPage - 1);
                }
            },
            nextPage() {
                if (this.currentPage < this.totalPages) {
                    this.renderPage(this.currentPage + 1);
                }
            },
            getFieldSample(fieldName) {
                const samples = {
                    'XM': '张三',
                    'KSH': '25420102970235',
                    'BH': 'BK20250001',
                    'ZJHM': '341221200703258584',
                    'G_XYMC': '商学院',
                    'LQZYMC': '市场营销',
                    'PCMC': '三二分段与五年一贯制',
                    'G_Sheng': '湖北省',
                    'G_ShiZhou': '武汉市',
                    'G_QuXian': '江岸区',
                    'GZ': '计算机操作员',
                    'XZDM': '三年',
                    'CCDM': '专科'
                };
                return samples[fieldName] || '';
            },
            saveConfig() {
                const api_name = '/Notice/SaveFieldConfig';
                const url = TYBM_Env.GetApiUrl(api_name);
                
                TYBM_Tools.AxiosPostEx(this, api_name, url, {
                    templateId: this.templateId,
                    fieldConfig: JSON.stringify(this.fieldPositions)
                }, { loading: false, show_error: false })
                    .then(response => {
                        if (response.code === 'ok') {
                            this.$message.success('配置保存成功');
                            this.$emit('saved');
                        } else {
                            this.$message.error(response.errmsg || '保存失败');
                        }
                    })
                    .catch(() => {
                        this.$message.error('保存失败');
                    });
            },
            cancel() {
                this.$emit('cancel');
            }
        }
    };
</script>

<style scoped>
    .template-editor {
        display: flex;
        flex-direction: column;
        height: 100vh;
        max-height: calc(90vh - 80px);
        padding: 10px;
        box-sizing: border-box;
    }

    .editor-header {
        padding: 10px 0;
        border-bottom: 1px solid #eee;
        margin-bottom: 10px;
    }

    .editor-header h4 {
        margin: 0 0 5px 0;
        font-size: 16px;
    }

    .editor-header .tips {
        font-size: 12px;
        color: #909399;
    }

    .editor-body {
        flex: 1;
        display: flex;
        gap: 10px;
        overflow: hidden;
    }

    .field-list {
        width: 180px;
        background: #f5f7fa;
        border-radius: 8px;
        padding: 10px;
        overflow-y: auto;
    }

    .field-list h5 {
        margin: 0 0 10px 0;
        font-size: 14px;
        color: #606266;
    }

    .fields {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .field-item {
        padding: 8px 12px;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        border: 1px solid #dcdfe6;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .field-item:hover:not(.disabled) {
        border-color: #409EFF;
        background: #ecf5ff;
    }

    .field-item.active {
        border-color: #409EFF;
        background: #409EFF;
        color: white;
    }

    .field-item.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .field-icon {
        font-size: 14px;
    }

    .used-tag {
        font-size: 11px;
        color: #909399;
        margin-left: auto;
    }

    .pdf-editor {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: #f5f7fa;
        border-radius: 8px;
        overflow: hidden;
    }

    .pdf-container {
        flex: 1;
        position: relative;
        overflow: auto;
        background: #e8e8e8;
        cursor: crosshair;
    }

    .pdf-container canvas {
        display: block;
        background: white;
    }

    .field-marker {
        position: absolute;
        padding: 4px 8px;
        background: rgba(64, 158, 255, 0.9);
        color: white;
        border-radius: 4px;
        cursor: move;
        transform: translate(-50%, -50%);
        z-index: 100;
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 8px;
        user-select: none;
        border: 1px solid rgba(255,255,255,0.3);
        pointer-events: auto;
    }

    .field-marker:hover {
        background: rgba(64, 158, 255, 1);
    }

    .field-label {
        pointer-events: none;
    }

    .field-delete {
        font-size: 16px;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.8);
    }

    .field-delete:hover {
        color: white;
    }

    .cursor-indicator {
        position: absolute;
        padding: 4px 8px;
        background: rgba(103, 194, 58, 0.9);
        color: white;
        border-radius: 4px;
        transform: translate(-50%, -50%);
        z-index: 99;
        pointer-events: none;
        font-size: 14px;
    }

    .position-info {
        padding: 5px 10px;
        background: #e8e8e8;
        font-size: 12px;
        color: #606266;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
    }

    .page-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .page-controls button {
        width: 60px;
        height: 24px;
        border: 1px solid #dcdfe6;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
    }

    .page-controls button:hover:not(:disabled) {
        background: #ecf5ff;
        border-color: #409EFF;
    }

    .page-controls button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .zoom-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .zoom-controls button {
        width: 28px;
        height: 24px;
        border: 1px solid #dcdfe6;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
    }

    .zoom-controls button:hover:not(:disabled) {
        background: #ecf5ff;
        border-color: #409EFF;
    }

    .zoom-controls button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .field-properties {
        width: 200px;
        background: #f5f7fa;
        border-radius: 8px;
        padding: 10px;
        overflow-y: auto;
    }

    .field-properties h5 {
        margin: 0 0 10px 0;
        font-size: 14px;
        color: #606266;
    }

    .properties {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .property {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .property label {
        font-size: 12px;
        color: #909399;
    }

    .property input {
        padding: 5px 8px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        font-size: 13px;
    }

    .property span {
        font-size: 13px;
        color: #606266;
        word-break: break-all;
    }

    .editor-footer {
        padding: 10px 0;
        border-top: 1px solid #eee;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 10px;
    }
</style>